/* eslint-disable react/no-unknown-property */
import React, { FC, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';

import Navbar from '../../components/Navbar';
import HeaderHome from '../../components/HeaderHome';
import WhyHome from '../../components/WhyHome';
import GuideHome from '../../components/GuideHome';
import ReadytoGetStarted from '../../components/ReadytoGetStarted';
import BitcoinPriceSection from '../../components/BitcoinPriceSection';
import Footer from '../../components/Footer';
import InterlinkSection from '../../components/InterlinkSection';
import { useMPCWidget } from '../../helpers/useMPCWidget';
import {
  cryptoData,
  cryptoSectionTranslations,
  getCryptoUrl,
} from '../../data/cryptoData';
import {
  providerData,
  providerSectionTranslations,
  getProviderUrl,
} from '../../data/providerData';

interface Props {
  data: Queries.HomeQueryQuery;
  locale: string;
  provider?: string;
  showLoginWidget?: boolean;
}

const Home: FC<Props> = ({ data, locale, provider, showLoginWidget }) => {
  const { file } = data;
  // const [hasScrolled, setHasScrolled] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const { hasAccount, isLoading, user } = useMPCWidget();

  const localeKey = (locale as 'en' | 'es' | 'ru' | 'zh') || 'en';
  const cryptoLinkPrefix =
    cryptoSectionTranslations.linkPrefix[localeKey] || 'Buy';

  const cryptoLinks = useMemo(
    () =>
      cryptoData.map(crypto => ({
        slug: crypto.slug,
        label: `${cryptoLinkPrefix} ${crypto.names[localeKey] || crypto.names.en}`,
        url: getCryptoUrl(crypto.slug, locale),
      })),
    [locale, localeKey, cryptoLinkPrefix]
  );

  const providerLinkPrefix =
    providerSectionTranslations.linkPrefix[localeKey] || 'Buy Crypto with';

  const providerLinks = useMemo(
    () =>
      providerData
        .filter(p => p.slug !== provider)
        .map(p => ({
          slug: p.slug,
          label: `${providerLinkPrefix} ${p.name}`,
          url: getProviderUrl(p.slug, locale),
        })),
    [locale, provider, providerLinkPrefix]
  );

  useEffect(() => {
    const handleLoad = () => {
      setTimeout(() => {
        setShowVideo(true);
      }, 2000);
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  return (
    <>
      <Helmet>
      <html lang={locale} />
        <link
          rel="preconnect"
          href="https://fonts.bitcoin.com/Elza/Elza_Condensed_Black/crossmedia/Elza-CondensedBlack.woff"
          as="font"
          type="font/woff"
          crossOrigin="anonymous"
        />
        <link
          rel="preconnect"
          href="https://fonts.bitcoin.com/Satoshi_Variable/Fonts/WEB/fonts/Satoshi-Medium.woff"
          as="font"
          type="font/woff"
          crossOrigin="anonymous"
        />
        <style>
          {`
            @font-face {
              font-family: 'Elza Condensed';
              src: url('https://fonts.bitcoin.com/Elza/Elza_Condensed_Black/crossmedia/Elza-CondensedBlack.woff') format('woff');
              font-style: normal;
              font-display: swap;
            }
            @font-face {
              font-family: 'Satoshi Variable Medium';
              src: url('https://fonts.bitcoin.com/Satoshi_Variable/Fonts/WEB/fonts/Satoshi-Medium.woff') format('woff');
              font-weight: normal;
              font-style: normal;
              font-display: swap;
            }
            /* RTL Global Styles */
            html[dir="rtl"] {
              text-align: right;
            }
            html[dir="rtl"] * {
              text-align: right;
            }
            html[dir="rtl"] .ltr-text {
              direction: ltr;
              text-align: left;
            }
          `}
        </style>
        <script
          src={
            process.env.MPC_WIDGET_URL ||
            'https://widget.bitcoin.com/widget.v0.7.14.iife.js'
          }
          type="text/javascript"
        />
      </Helmet>
      <Navbar navbar={file?.childJson?.navbar || null} locale={locale} />

      <main>
        <HeaderHome
          headerHome={file?.childJson?.headerHome || null}
          provider={provider}
          showLoginWidget={showLoginWidget}
          isLoading={isLoading}
          hasAccount={hasAccount}
          user={user}
          showVideo={showVideo}
        />
        <WhyHome
          whyHome={file?.childJson?.whyHome || null}
          // hasScrolled={hasScrolled}
        />
        <InterlinkSection
          title={
            cryptoSectionTranslations.homepage.title[localeKey] ||
            'Buy 120+ cryptocurrencies'
          }
          subtitle={
            cryptoSectionTranslations.homepage.subtitle[localeKey] ||
            'Securely purchase and manage a wide range of cryptocurrencies with ease.'
          }
          items={cryptoLinks}
          showSeeMore
          seeMoreLabel={
            cryptoSectionTranslations.homepage.seeMore[localeKey] || 'See more'
          }
        />
        <GuideHome
          guideHome={file?.childJson?.guideHome || null}
          // hasScrolled={hasScrolled}
          showVideo={showVideo}
        />
        <BitcoinPriceSection
          bitcoinpricesection={file?.childJson?.bitcoinpricesection || null}
          // hasScrolled={hasScrolled}
        />
        <ReadytoGetStarted
          readytogetstarted={file?.childJson?.readytogetstarted || null}
          // hasScrolled={hasScrolled}
        />
      </main>

      {provider && (
        <InterlinkSection
          title={
            providerSectionTranslations.title[localeKey] ||
            'More ways to buy cryptocurrencies'
          }
          subtitle={
            providerSectionTranslations.subtitle[localeKey] ||
            'Explore additional options to buy crypto assets quickly and securely'
          }
          items={providerLinks}
        />
      )}

      <Footer
        footer={file?.childJson?.footer || null}
        // hasScrolled={hasScrolled}
      />
    </>
  );
};

export default Home;
